<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { VesselDataPayload } from "@/types/interface/vessel";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { useVesselStore } from "@/stores/master/vessel";
import { formatDate, mapOptionMultiselect } from "@/utils/helper";
import { useVesselClass } from "@/stores/master/vessel-class";
import { useVesselType } from "@/stores/master/vessel-type";
import { usePortStore } from "@/stores/master/port";
import { useShipOwnerCompanyStore } from "@/stores/master/ship-owner-company";

const idShip = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const vesselStore = useVesselStore();
const vesselClassStore = useVesselClass();
const vesselTypeStore = useVesselType();
const shipOwnerStore = useShipOwnerCompanyStore();
const listClass = ref<any>();
const listTypeVessel = ref<any>();
const detailVessel = ref<any>();
const listPort = ref<any>();
const portStore = usePortStore();
const listShipOwner = ref([]);

const validationSchema = toTypedSchema(
  zod.object({
    name: zod.string().nonempty({ message: "Vessel Name is required" }),
    imo: zod.string(),
    mmsi: zod.string(),
    call_sign: zod.string().nonempty({ message: "Call Sign is required" }),
    keel_laid: zod.string().nonempty({ message: "Keel Laid is required" }),
    registry_under_company: zod
      .string()
      .nonempty({ message: "Registry Under Company Date is required" }),
    ship_owner: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    vessel_class_id: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    port_id: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    vessel_type_id: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    load_cap: zod.preprocess((val) => Number(val), zod.number()),
    service_speed: zod.preprocess((val) => Number(val), zod.number()),
    gross_tonnage: zod.preprocess((val) => Number(val), zod.number()),
    net_tonnage: zod.preprocess((val) => Number(val), zod.number()),
    deadweight: zod.preprocess((val) => Number(val), zod.number()),
    len_overall: zod.preprocess((val) => Number(val), zod.number()),
    breadth: zod.preprocess((val) => Number(val), zod.number()),
    depth: zod.preprocess((val) => Number(val), zod.number()),
    me_maker: zod.string(),
    type: zod.string(),
    power: zod.string(),
    year: zod.string(),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: name } = useField<string>("name");
const { value: imo } = useField<string>("imo");
const { value: mmsi } = useField<string>("mmsi");
const { value: call_sign } = useField<string>("call_sign");
const { value: keel_laid } = useField<string>("keel_laid");
const { value: registry_under_company } = useField<string>(
  "registry_under_company"
);
const { value: ship_owner } = useField<any>("ship_owner");
const { value: vessel_class_id } = useField<any>("vessel_class_id");
const { value: port_id } = useField<string>("port_id");
const { value: vessel_type_id } = useField<any>("vessel_type_id");
const { value: load_cap } = useField<number>("load_cap");
const { value: service_speed } = useField<number>("service_speed");
const { value: gross_tonnage } = useField<number>("gross_tonnage");
const { value: net_tonnage } = useField<number>("net_tonnage");
const { value: deadweight } = useField<number>("deadweight");
const { value: len_overall } = useField<number>("len_overall");
const { value: breadth } = useField<number>("breadth");
const { value: depth } = useField<number>("depth");
const { value: me_maker } = useField<string>("me_maker");
const { value: type } = useField<string>("type");
const { value: power } = useField<string>("power");
const { value: year } = useField<string>("year");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadShip: VesselDataPayload = {
    name: name.value,
    imo: imo.value,
    mmsi: mmsi.value,
    call_sign: call_sign.value,
    keel_laid: keel_laid.value,
    ship_owner_company_id: ship_owner.value?.value,
    registry_under_company: registry_under_company.value,
    vessel_class_id: vessel_class_id.value?.value,
    port_id: port_id.value?.value,
    vessel_type_id: vessel_type_id.value?.value,
    load_cap: load_cap.value,
    service_speed: service_speed.value,
    gross_tonnage: gross_tonnage.value,
    net_tonnage: net_tonnage.value,
    deadweight: deadweight.value,
    len_overall: len_overall.value,
    breadth: breadth.value,
    depth: depth.value,
    me_maker: me_maker.value,
    type: type.value,
    power: power.value,
    year: year.value,
  };

  const responseApi = ref<any>();

  if (isEdit.value == true) {
    responseApi.value = await vesselStore.dispatchUpdate(
      idShip.value,
      payloadShip
    );
  } else {
    responseApi.value = await vesselStore.dispatchCreate(payloadShip);
  }

  setTimeout(() => {
    toast.success("Ship Created Successfully");
  }, 1000);

  setTimeout(() => {
    isLoading.value = false;
    router.push("/master/vessel");
  }, 1500);
});

const loadListClass = async () => {
  const { success, message } = await vesselClassStore.dispatchGetAll(1, 99);
  if (success) {
    listClass.value = mapOptionMultiselect(
      await vesselClassStore.vesselClassList.items,
      "id",
      "code",
      "name"
    );
  }
};

const loadListVesselType = async () => {
  const { success, message } = await vesselTypeStore.dispatchGetAll(1, 99);
  if (success) {
    listTypeVessel.value = mapOptionMultiselect(
      await vesselTypeStore.vesselTypes.items,
      "id",
      "code",
      "name"
    );
  }
};

const loadListPort = async (query: string) => {
  let queryParam = "&keyword=" + query;
  const { success, message } = await portStore.dispatchGetAll(
    1,
    300,
    queryParam
  );
  if (success) {
    listPort.value = mapOptionMultiselect(
      await portStore.ports.items,
      "id",
      "name",
      "country"
    );
  }
};

let debounceTimeoutPort: any;

const handleSearchPort = (query: string) => {
  clearTimeout(debounceTimeoutPort);
  debounceTimeoutPort = setTimeout(() => {
    loadListPort(query);
  }, 500);
};

const loadListShipOwner = async () => {
  const { success, message } = await shipOwnerStore.dispatchGetList(1, 99, "");
  listShipOwner.value = mapOptionMultiselect(
    shipOwnerStore.owners.items,
    "id",
    "name"
  );
};

const loadDetailVessel = async () => {
  const { success, message } = await vesselStore.dispatchDetail(idShip.value);
  detailVessel.value = await vesselStore?.vessel?.data;
  name.value = detailVessel.value?.name;
  breadth.value = detailVessel.value?.breadth;
  imo.value = detailVessel.value?.imo;
  mmsi.value = detailVessel.value?.mmsi;
  call_sign.value = detailVessel.value?.call_sign;
  keel_laid.value = formatDate(detailVessel.value?.keel_laid);
  ship_owner.value = mapOptionMultiselect(
    detailVessel.value?.ship_owner_company
  );
  registry_under_company.value = formatDate(
    detailVessel.value?.registry_under_company
  );
  vessel_class_id.value = mapOptionMultiselect(
    detailVessel.value?.vesselClass,
    "id",
    "name"
  );
  port_id.value = mapOptionMultiselect(
    detailVessel.value?.port,
    "id",
    "name",
    "country"
  );
  vessel_type_id.value = mapOptionMultiselect(
    detailVessel.value?.vesselType,
    "id",
    "name"
  );
  load_cap.value = detailVessel.value?.load_cap;
  service_speed.value = detailVessel.value?.service_speed;
  gross_tonnage.value = detailVessel.value?.gross_tonnage;
  net_tonnage.value = detailVessel.value?.net_tonnage;
  deadweight.value = detailVessel.value?.deadweight;
  len_overall.value = detailVessel.value?.len_overall;
  depth.value = detailVessel.value?.depth;
  me_maker.value = detailVessel.value?.me_maker;
  type.value = detailVessel.value?.type;
  power.value = detailVessel.value?.power;
  year.value = detailVessel.value?.year;
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idShip.value = router.currentRoute.value.params.id.toString();
    loadDetailVessel();
  }
  loadListShipOwner();
  loadListClass();
  loadListVesselType();
  loadListPort();
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Ship</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/ship"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Ship
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name" class="form-label label text-secondary"
                    >Vessel Name <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Vessel Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="imo" class="form-label label text-secondary"
                    >IMO Number <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <input
                      name="imo"
                      v-model="imo"
                      placeholder="IMO Number"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.imo }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="mmsi" class="form-label label text-secondary"
                    >MMSI <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <input
                      type="text"
                      name="mmsi"
                      v-model="mmsi"
                      placeholder="MMSI"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.mmsi }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="call_sign" class="form-label label text-secondary"
                    >Call Sign <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <input
                      type="text"
                      name="call_sign"
                      v-model="call_sign"
                      placeholder="Call Sign"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.call_sign }}</span>
                </div>
              </div>
              <div class="col-lg-12">
                <div class="form-group">
                  <label for="keel_laid" class="form-label label text-secondary"
                    >Keel Laid <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <input
                      type="date"
                      name="keel_laid"
                      v-model="keel_laid"
                      placeholder="Keel Laid"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.keel_laid }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="ship_owner"
                    class="form-label label text-secondary"
                    >Ship Owner <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="ship_owner"
                      :options="listShipOwner"
                      placeholder="Choose Ship Owner"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.ship_owner }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="registry_under_company"
                    class="form-label label text-secondary"
                    >Registry Under Company
                    <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <input
                      name="registry_under_company"
                      v-model="registry_under_company"
                      class="form-control text-dark"
                      type="date"
                    />
                  </div>
                  <span class="text-danger">{{
                    errors.registry_under_company
                  }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="vessel_class_id"
                    class="form-label label text-secondary"
                    >Vessel Class <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="vessel_class_id"
                      :options="listClass"
                      placeholder="Vessel Class"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.vessel_class_id }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="port_id" class="form-label label text-secondary"
                    >Port of Registry <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="port_id"
                      :options="listPort"
                      placeholder="Port of Registry"
                      @search="handleSearchPort"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.port_id }}</span>
                </div>
              </div>
              <div class="col-lg-12">
                <div class="form-group">
                  <label
                    for="vessel_type_id"
                    class="form-label label text-secondary"
                    >Type Of Vessel <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="vessel_type_id"
                      :options="listTypeVessel"
                      placeholder="Type of Vessel"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.vessel_type_id }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label for="load_cap" class="form-label label text-secondary"
                    >Load Capacity (mt)
                    <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <input
                      name="load_cap"
                      v-model="load_cap"
                      placeholder="Load Capacity (mt)"
                      class="form-control text-dark"
                      type="number"
                      step="any"
                    />
                  </div>
                  <span class="text-danger">{{ errors.load_cap }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label
                    for="service_speed"
                    class="form-label label text-secondary"
                    >Service Speed (knots)</label
                  >
                  <div class="form-group">
                    <input
                      name="service_speed"
                      v-model="service_speed"
                      placeholder="Service Speed (knots)"
                      class="form-control text-dark"
                      type="number"
                      step="any"
                    />
                  </div>
                  <span class="text-danger">{{ errors.service_speed }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label
                    for="gross_tonnage"
                    class="form-label label text-secondary"
                    >Gross Tonnage</label
                  >
                  <div class="form-group">
                    <input
                      name="gross_tonnage"
                      v-model="gross_tonnage"
                      placeholder="Gross Tonnage"
                      class="form-control text-dark"
                      type="number"
                      step="any"
                    />
                  </div>
                  <span class="text-danger">{{ errors.gross_tonnage }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label
                    for="net_tonnage"
                    class="form-label label text-secondary"
                    >Net Tonnage</label
                  >
                  <div class="form-group">
                    <input
                      name="net_tonnage"
                      v-model="net_tonnage"
                      placeholder="Net Tonnage"
                      class="form-control text-dark"
                      type="number"
                      step="any"
                    />
                  </div>
                  <span class="text-danger">{{ errors.net_tonnage }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label
                    for="deadweight"
                    class="form-label label text-secondary"
                    >Deadweight</label
                  >
                  <div class="form-group">
                    <input
                      name="deadweight"
                      v-model="deadweight"
                      placeholder="Deadweight"
                      class="form-control text-dark"
                      type="number"
                      step="any"
                    />
                  </div>
                  <span class="text-danger">{{ errors.deadweight }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label
                    for="len_overall"
                    class="form-label label text-secondary"
                    >Len Overall (meters)</label
                  >
                  <div class="form-group">
                    <input
                      name="len_overall"
                      v-model="len_overall"
                      placeholder="Len Overall (meters)"
                      class="form-control text-dark"
                      type="number"
                      step="any"
                    />
                  </div>
                  <span class="text-danger">{{ errors.len_overall }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label for="breadth" class="form-label label text-secondary"
                    >Breadth (meters)</label
                  >
                  <div class="form-group">
                    <input
                      name="breadth"
                      v-model="breadth"
                      placeholder="Breadth (meters)"
                      class="form-control text-dark"
                      type="number"
                      step="any"
                    />
                  </div>
                  <span class="text-danger">{{ errors.breadth }}</span>
                </div>
              </div>
              <div class="col-lg-3">
                <div class="form-group">
                  <label for="depth" class="form-label label text-secondary"
                    >Depth (meters)</label
                  >
                  <div class="form-group">
                    <input
                      name="depth"
                      v-model="depth"
                      placeholder="Depth (meters)"
                      class="form-control text-dark"
                      type="number"
                      step="any"
                    />
                  </div>
                  <span class="text-danger">{{ errors.depth }}</span>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="form-group">
                  <label for="me_maker" class="form-label label text-secondary"
                    >Main Engine Maker</label
                  >
                  <div class="form-group">
                    <input
                      name="me_maker"
                      v-model="me_maker"
                      placeholder="Main Engine Maker"
                      class="form-control text-dark"
                      type="text"
                    />
                  </div>
                  <span class="text-danger">{{ errors.me_maker }}</span>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="form-group">
                  <label for="type" class="form-label label text-secondary"
                    >Engine Type</label
                  >
                  <div class="form-group">
                    <input
                      name="type"
                      v-model="type"
                      placeholder="Engine Type"
                      class="form-control text-dark"
                      type="text"
                    />
                  </div>
                  <span class="text-danger">{{ errors.type }}</span>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="form-group">
                  <label for="power" class="form-label label text-secondary"
                    >Power</label
                  >
                  <div class="form-group">
                    <input
                      name="power"
                      v-model="power"
                      placeholder="Power"
                      class="form-control text-dark"
                      type="text"
                    />
                  </div>
                  <span class="text-danger">{{ errors.power }}</span>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="form-group">
                  <label for="year" class="form-label label text-secondary"
                    >Year</label
                  >
                  <div class="form-group">
                    <input
                      name="year"
                      v-model="year"
                      placeholder="Year"
                      class="form-control text-dark"
                      type="text"
                    />
                  </div>
                  <span class="text-danger">{{ errors.year }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
